import { axios } from '@util'
import moment from 'moment'



export const STATUS = {
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  CANCELLED: 'Cancelled',
  DELIVERED: 'Delivered',
  PENDING: 'Pending',
  FAILED: 'Failed'
}

const PLACEHOLDER = null

/**
 * Pull every page of a paginated list endpoint.
 */
const fetchAll = async (url) => {
  let page = 1
  let results = []
  let next = true

  while (next) {
    const {
      data: {
        data: { results: pageResults, next: pageNext }
      }
    } = await axios({ url, params: { page, page_size: 100 } })

    results = results.concat(pageResults || [])
    next = !!pageNext
    page += 1
  }

  return results
}

/**
 * Derive a human-readable frequency label from a schedule record.
 */
const frequencyLabel = (schedule) => {
  if (!schedule) return 'One-time'
  return schedule.occurrence || 'One-time'
}

/**
 * Map the stored export_type code.
 */
const EXPORT_TYPE_LABELS = {
  market_export: 'Market Split',
  custom_export: 'Custom Split'
}
// A Full Data Export is functionally a custom split, but must display as
// "Full Data Export" everywhere Export Type is shown. The flag is persisted in
// the export/schedule parameters as is_full_data_export.
const exportTypeLabel = (params) => {
  if (params && params.is_full_data_export) return 'Full Data Export'
  const code = params && params.export_type
  if (!code) return null
  return EXPORT_TYPE_LABELS[code] || code
}

/**
 * Build the Full Data Export friendly filename from the run's parameters and
 * timestamp — mirroring the backend (_fde_export_zip_name in service.py):
 *   LimeIQ - <Company> - Full Data Export - <DD-MM-YYYY HHMM>.zip
 * <Company> is the SELECTED company for the export (for a superuser, the
 * selected underwriter; for a non-superuser, their own company) — the backend
 * resolves it from company_id. The frontend only has company_id here,
 * so it uses a company name if one is present on the params (company_name),
 * otherwise omits that segment — the authoritative name is the backend-baked
 * files[].name shown once the run completes. This synthesized value is only a
 * PENDING/FAILED preview. Returns null if not an FDE.
 */
const fullDataExportName = (params, runDate, companyName) => {
  if (!params || !params.is_full_data_export) return null
  const company =
    companyName || params.company_name || params.companyName || null
  const stamp = runDate ? moment(runDate).format('DD-MM-YYYY_HHmm') : ''
  const parts = ['LimeIQ']
  if (company) parts.push(String(company).trim())
  parts.push('Full Data Export')
  if (stamp) parts.push(stamp)
  return parts.join(' - ') + '.zip'
}

/**
 * Build the NORMAL (non-FDE) export friendly filename (mirroring the backend
 * _normal_export_zip_name):
 *   LimeIQ - <Export Type> - <DD-MM-YYYY HHMM>.zip
 * Unlike the FDE name, normal exports carry NO company segment. <Export Type>
 * maps market_export→Market Split, custom_export→Custom Split. Used for the
 * PENDING/FAILED preview before the backend has baked files[].name.
 */
const normalExportName = (params, runDate) => {
  const p = params || {}
  const typeLabel = EXPORT_TYPE_LABELS[p.export_type] || 'Export'
  const dateTime = runDate ? moment(runDate).format('DD-MM-YYYY_HHmm') : ''
  // Normal exports carry NO company segment (unlike FDE): LimeIQ - <Type> - <stamp>.zip
  const segments = ['LimeIQ', typeLabel]
  if (dateTime) segments.push(dateTime)
  return segments.join(' - ') + '.zip'
}

/**
 * Derive the schedule-level status badge.
 */
const scheduleStatus = (schedule) => {
  if (!schedule) return STATUS.PAUSED
  if (schedule.cancelled) return STATUS.CANCELLED
  return schedule.status ? STATUS.ACTIVE : STATUS.PAUSED
}

/**
 * Derive the delivery/processing status badge for a single run.
 */
const runStatus = (run) => {
  if (run.cancelled) return STATUS.CANCELLED
  if (run.is_failed) return STATUS.FAILED
  if (run.is_pending) return STATUS.PENDING
  // A finished run (not pending/failed/cancelled) is Delivered, whether or
  // not a delivery timestamp was recorded.
  return STATUS.DELIVERED
}

/**
 * Best-effort display name + download link + run date for a generated report.
 * Mirrors the logic the legacy Previous Reports page used.
 */
const describeRun = (run) => {
  let link = null
  const files = Array.isArray(run.files) ? run.files : []

  // Keep the real download link when the export produced a file.
  let rawName = null
  if (run.folder_path) {
    if (files.length) {
      rawName = files[files.length - 1].name
      link = files[files.length - 1].link
    } else {
      rawName = run.folder_path
    }
  } else if (files.length) {
    rawName = files[0].name
    link = files[0].link
  }

  // Resolve the run date: prefer the stored timestamp, else parse the
  // "MM-DD-YYYY_..." prefix off the folder/file name.
  let runDate = run.created_on || null
  if (!runDate && rawName) {
    const parts = rawName.split(/[\\/]/).pop().split('_')[0].split('-')
    if (parts.length === 3) {
      const parsed = moment(`${parts[2]}-${parts[0]}-${parts[1]}`)
      if (parsed.isValid()) runDate = parsed.toISOString()
    }
  }

  // Show the friendly filename irrespective of status. A completed run has the
  // backend-saved name in files[].name; a PENDING/FAILED run has no file yet,
  // so synthesize the same name from parameters + timestamp.
  const isFullDataExport = !!(run.parameters && run.parameters.is_full_data_export)
  // FDE and normal exports share ONE consistent zip-name structure, baked by
  // the backend into files[].name:
  //   FDE:    LimeIQ - <Company> - Full Data Export - <DD-MM-YYYY HHMM>.zip
  //   Normal: LimeIQ - <Company> - <Export Type>     - <DD-MM-YYYY HHMM>.zip
  // (<Company> = the running company; spaced hyphen separators.) Prefer the
  // stored name so the UI matches the download exactly; when there is no file
  // yet (PENDING/FAILED) fall back to a synthesized preview.
  // NEVER use run.folder_path — it's the raw "MM-DD-YYYY_HH-MM-SS" name.
  const storedFileName = files.length
    ? (files[files.length - 1].name || files[0].name)
    : null
  let name
  if (storedFileName) {
    name = storedFileName.split(/[\\/]/).pop()
  } else {
    // COMPANY segment = the SELECTED company for the export. run.company_name is
    // company_id-resolved: for a superuser that's the selected underwriter, for a
    // non-superuser it's their own company — exactly what the filename must show.
    // Fall back to requester_company_name only for older exports missing it.
    const runnerCompany =
      run.company_name || (run.parameters && run.parameters.requester_company_name)
    if (isFullDataExport) {
      name = fullDataExportName(run.parameters, runDate, runnerCompany) || 'Full Data Export.zip'
    } else {
      // Normal exports have no company segment, so runnerCompany is not passed.
      name = normalExportName(run.parameters, runDate)
    }
  }
  // The displayed name must match the actual zip filename EXACTLY. The real
  // file uses "<DD-MM-YYYY>_<HHMM>" (no colon — colons are illegal in Windows
  // filenames / URLs), so we show it verbatim, with no display-only transform.

  return {
    id: run.id,
    name,
    link,
    runDate,
    runType: run.run_type || (run.saved_report ? 'Scheduled' : 'One-time'),
    status: runStatus(run),
    recipients:
      Array.isArray(run.recipients) && run.recipients.length
        ? run.recipients
        : run.recipients && !Array.isArray(run.recipients)
        ? run.recipients
        : PLACEHOLDER,
    deliveredAt: run.delivered_at || PLACEHOLDER,
    entityType: run.entity_type || PLACEHOLDER,
    companyName: run.company_name || PLACEHOLDER,
    exportType:
      exportTypeLabel(run.parameters) ||
      PLACEHOLDER,
    loggedInUser: run.logged_in_user || PLACEHOLDER,
    parameters: run.parameters || null,
    note: run.note || null,
    isPending: !!run.is_pending,
    isFailed: !!run.is_failed,
    savedReportId: run.saved_report || null
  }
}

/**
 * Flatten a saved schedule's payload into the same `{name: value}` map a
 * one-time export's `parameters` field uses, so the View Details modal shows
 * the same fields (criteria, flags, role, columns, etc.) for both.
 */
const flattenSchedulePayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null

  const flat = {}

  // Criteria and flags are lists of { name, value }.
  ;['parameters', 'flag'].forEach((key) => {
    if (Array.isArray(payload[key])) {
      payload[key].forEach((item) => {
        if (!item || !item.name) return
        if (flat[item.name]) flat[item.name] = [].concat(flat[item.name], item.value)
        else flat[item.name] = item.value
      })
    }
  })

  // Scalar/list config carried alongside the criteria.
  ;[
    'role',
    'role_value',
    'columns_to_show',
    'umr_criteria',
    'export_by',
    'group_by',
    'group_by_selection',
    'uw_option',
    'company_id',
    'export_type',
    'jurisdiction',
    'all_exports'
  ].forEach((key) => {
    if (payload[key] !== undefined && payload[key] !== null && payload[key] !== '')
      flat[key] = payload[key]
  })

  return Object.keys(flat).length ? flat : null
}

/**
 * Fetch and normalize the full export log.
 *
 * @returns {Promise<Array>} array of main rows, each with a `runs` array.
 */
export const fetchExportLog = async () => {
  const [schedules, reports] = await Promise.all([
    fetchAll('/saved_exports/'),
    fetchAll('/downloaded_reports/')
  ])

  const runs = reports.map(describeRun)

  // Group runs under their owning schedule when the link exists.
  const runsBySchedule = {}
  const orphanRuns = []
  runs.forEach((run) => {
    if (run.savedReportId != null) {
      if (!runsBySchedule[run.savedReportId])
        runsBySchedule[run.savedReportId] = []
      runsBySchedule[run.savedReportId].push(run)
    } else {
      orphanRuns.push(run)
    }
  })

  const rows = []

  // 1. Scheduled exports — one row per schedule, runs nested for history.
  schedules.forEach((schedule) => {
    const scheduleRuns = (runsBySchedule[schedule.id] || []).sort((a, b) =>
      moment(b.runDate).diff(moment(a.runDate))
    )
    const lastRun = scheduleRuns[0]

    // The schedule's configured criteria live in its JSON payload. Flatten
    // it into the same shape a one-time export's `parameters` uses, so the
    // View Details modal renders identically for schedules and one-offs.
    let scheduleParams = null
    if (schedule.payload) {
      try {
        scheduleParams = flattenSchedulePayload(JSON.parse(schedule.payload))
      } catch (e) {
        scheduleParams = null
      }
    }

    // Show the next scheduled execution as an upcoming entry at the top of the
    // run history, so users can see when the report would run next. It is
    // labelled to match the schedule state: Paused / Cancelled / Pending.
    const schedStatus = scheduleStatus(schedule)
    let upcomingStatus = STATUS.PENDING
    if (schedStatus === STATUS.PAUSED) upcomingStatus = STATUS.PAUSED
    else if (schedStatus === STATUS.CANCELLED) upcomingStatus = STATUS.CANCELLED
    const historyRuns = scheduleRuns.slice()
    if (schedule.next_run) {
      historyRuns.unshift({
        id: `next-${schedule.id}`,
        name: null,
        link: null,
        runDate: schedule.next_run,
        runType: 'Scheduled',
        status: upcomingStatus,
        recipients: schedule.recipients || PLACEHOLDER,
        deliveredAt: PLACEHOLDER,
        loggedInUser: schedule.logged_in_user || PLACEHOLDER,
        isPending: true,
        isFailed: false,
        isUpcoming: true
      })
    }

    rows.push({
      key: `schedule-${schedule.id}`,
      scheduleId: schedule.id,
      name: schedule.name || '—',
      companyName: schedule.company_name || PLACEHOLDER,
      entityType: schedule.entity_type || PLACEHOLDER,
      exportType:
        exportTypeLabel(scheduleParams) ||
        PLACEHOLDER,
      frequency: frequencyLabel(schedule),
      nextRun: schedule.next_run || PLACEHOLDER,
      lastRun: lastRun ? lastRun.runDate : null,
      status: schedStatus,
      recipients: schedule.recipients || PLACEHOLDER,
      // Who triggered the latest run, else who created the schedule.
      loggedInUser:
        (lastRun && lastRun.loggedInUser) ||
        schedule.logged_in_user ||
        PLACEHOLDER,
      timeOfDay: schedule.time_of_day || PLACEHOLDER,
      startDate: schedule.start_date || PLACEHOLDER,
      endDate: schedule.end_date || PLACEHOLDER,
      parameters: scheduleParams,
      isScheduled: true,
      runs: historyRuns
    })
  })

  // 2. One-time / orphan exports — each generated report is its own row.
  orphanRuns.forEach((run) => {
    rows.push({
      key: `report-${run.id}`,
      scheduleId: null,
      name: run.name || '—',
      companyName: run.companyName,
      entityType: run.entityType,
      exportType: run.exportType,
      frequency: 'One-time',
      nextRun: PLACEHOLDER,
      lastRun: run.runDate,
      status: run.status,
      recipients: run.recipients,
      deliveredAt: run.deliveredAt,
      loggedInUser: run.loggedInUser,
      note: run.note,
      parameters: run.parameters,
      isScheduled: false,
      runs: [run]
    })
  })

  return rows
}

/**
 * Fetch the Export Log as a flat, chronological list of actual export runs
 * (LIQ-1908 feedback #6). Unlike fetchExportLog, this deliberately:
 *   - does NOT fetch /saved_exports/ — no scheduled-export rows and no
 *     upcoming/next-run synthetic rows appear in the log;
 *   - returns one row per real export run (both one-time exports and the
 *     historical runs produced by schedules — those actually happened);
 *   - carries no schedule actions (the log is read-only).
 * Rows are sorted by run date, newest first (chronological order).
 */
export const fetchExportsOnly = async () => {
  const reports = await fetchAll('/downloaded_reports/')
  const rows = reports.map((report) => {
    const run = describeRun(report)
    return {
      key: `report-${run.id}`,
      id: run.id,
      name: run.name || '—',
      companyName: run.companyName,
      exportType: run.exportType,
      runDate: run.runDate,
      status: run.status,
      recipients: run.recipients,
      deliveredAt: run.deliveredAt,
      loggedInUser: run.loggedInUser,
      note: run.note,
      parameters: run.parameters,
      link: run.link,
      isPending: run.isPending,
      isFailed: run.isFailed
    }
  })

  // Chronological: newest run first. Rows without a date sort last.
  rows.sort((a, b) => {
    const ta = a.runDate ? new Date(a.runDate).getTime() : 0
    const tb = b.runDate ? new Date(b.runDate).getTime() : 0
    return tb - ta
  })

  return rows
}

/**
 * Convert the normalized rows into CSV text and trigger a client download.
 */
export const exportLogToCsv = (rows, { includeUser = false } = {}) => {
  // Export Log is now a chronological list of actual exports (no schedules),
  // so Frequency / Next Run are dropped and "Last Run" becomes "Run Date".
  const headers = [
    'Export Name',
    'Export Type',
    'Run Date',
    'Status',
    'Recipients'
  ]
  if (includeUser) headers.push('Logged-in User')

  const escape = (value) => {
    if (value == null) return ''
    const str = String(value).replace(/"/g, '""')
    return /[",\n]/.test(str) ? `"${str}"` : str
  }

  const fmt = (date) => (date ? moment(date).format('MM/DD/YYYY hh:mm A') : '')

  const lines = [headers.join(',')]
  rows.forEach((row) => {
    const cols = [
      escape(row.name),
      escape(row.exportType),
      escape(fmt(row.runDate)),
      escape(row.status),
      escape(
        Array.isArray(row.recipients)
          ? row.recipients.join('; ')
          : row.recipients
      )
    ]
    if (includeUser) cols.push(escape(row.loggedInUser))
    lines.push(cols.join(','))
  })

  const csv = lines.join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.setAttribute(
    'download',
    `export-log-${moment().format('YYYY-MM-DD')}.csv`
  )
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Run a schedule action (pause | resume | cancel) against a scheduled export.
 *
 * @param {int} scheduleId  SavedReportTemplate id.
 * @param {string} action   'pause' | 'resume' | 'cancel' | 'activate'.
 */
export const scheduleAction = (scheduleId, action) =>
  axios({
    url: `/export_log/schedule/${scheduleId}/${action}/`,
    method: 'POST'
  })

class ExportLogService {
  // Export Log page (read-only, chronological, exports only).
  fetch() {
    return fetchExportsOnly()
  }

  // Full builder incl. schedules — retained for the "Saved Reports &
  // Scheduling" table on the Export page (feedback #6 Part B).
  fetchWithSchedules() {
    return fetchExportLog()
  }

  toCsv(rows, options) {
    return exportLogToCsv(rows, options)
  }

  pause(scheduleId) {
    return scheduleAction(scheduleId, 'pause')
  }

  resume(scheduleId) {
    return scheduleAction(scheduleId, 'resume')
  }

  cancel(scheduleId) {
    return scheduleAction(scheduleId, 'cancel')
  }

  activate(scheduleId) {
    return scheduleAction(scheduleId, 'activate')
  }

  // Permanently delete a scheduled export.
  deleteSchedule(scheduleId) {
    return axios({
      url: `/export_log/schedule/${scheduleId}/`,
      method: 'DELETE'
    })
  }

  // Permanently delete a one-time export run.
  deleteExport(exportId) {
    return axios({
      url: `/export_log/export/${exportId}/`,
      method: 'DELETE'
    })
  }
}

export default new ExportLogService()
